import React from 'react';
import { cn } from '../../../utils/cn';
import { DeviceAccessTone } from './deviceAccessRows';

/**
 * The standard look for "what does this person have, and is it really there" — one bordered card,
 * an uppercase column header, one row per thing, state on the right.
 *
 * Shared so Zonas de Acesso and Leitores RFID stop being two different-looking inventions of the
 * same table. Both previously carried a column of small sync/revoke icon buttons per row, which
 * made a reference table read as a control panel and put destructive actions one mis-click away
 * while someone was reading. The bulk actions live in the section header instead.
 */

const toneClass = (tone: DeviceAccessTone, darkMode: boolean) => {
  switch (tone) {
    case 'green':
      return darkMode ? 'text-green-400' : 'text-green-600';
    case 'red':
      return darkMode ? 'text-red-400' : 'text-red-500';
    case 'amber':
      return darkMode ? 'text-amber-400' : 'text-amber-600';
    default:
      return darkMode ? 'text-gray-500' : 'text-gray-400';
  }
};

export interface DeviceAccessTableRow {
  id: string;
  /** Left column, first line. */
  primary: string;
  /** Left column, second line — reader type, or where it is. */
  secondary?: string | null;
  /** Middle column. */
  middle?: React.ReactNode;
  /** Omit on tables that are a reference list rather than a state report. */
  stateLabel?: string;
  tone?: DeviceAccessTone;
  errorMessage?: string | null;
  /** Per-row controls, e.g. sync/revoke for one zone instead of all of them. */
  actions?: React.ReactNode;
}

interface DeviceAccessTableProps {
  /** `state` omitted = no state column at all; the device table below owns that answer. */
  columns: { primary: string; middle?: string; state?: string };
  rows: DeviceAccessTableRow[];
  emptyLabel: string;
  darkMode?: boolean;
}

/** Any row offering controls gives the whole table an actions column, so rows stay aligned. */
const hasActions = (rows: DeviceAccessTableRow[]) => rows.some(r => !!r.actions);

const gridColumns = (columns: DeviceAccessTableProps['columns'], rows: DeviceAccessTableRow[]) => {
  const trailing = [columns.middle, columns.state, hasActions(rows) ? 'actions' : undefined]
    .filter(Boolean).length;
  return `grid-cols-[1fr${'_auto'.repeat(trailing)}]`;
};

export const DeviceAccessTable: React.FC<DeviceAccessTableProps> = ({
  columns,
  rows,
  emptyLabel,
  darkMode = false,
}) => (
  <div className={cn('rounded-lg border overflow-hidden', darkMode ? 'border-gray-700' : 'border-gray-200')}>
    <div
      className={cn(
        'grid gap-3 px-3 py-2 text-[10px] font-semibold uppercase tracking-widest',
        gridColumns(columns, rows),
        darkMode
          ? 'bg-gray-800/60 text-gray-500 border-b border-gray-700'
          : 'bg-gray-50 text-gray-400 border-b border-gray-200',
      )}
    >
      <span>{columns.primary}</span>
      {columns.middle && <span className="text-right">{columns.middle}</span>}
      {columns.state && <span className="text-right">{columns.state}</span>}
      {hasActions(rows) && <span className="text-right sr-only">Ações</span>}
    </div>

    {rows.length === 0 ? (
      <p className={cn('px-3 py-4 text-xs text-center', darkMode ? 'text-gray-600' : 'text-gray-400')}>
        {emptyLabel}
      </p>
    ) : (
      rows.map((row, idx) => (
        <div
          key={row.id}
          className={cn(
            'grid gap-3 items-center px-3 py-2 text-xs',
            gridColumns(columns, rows),
            idx > 0 && (darkMode ? 'border-t border-gray-800' : 'border-t border-gray-100'),
          )}
        >
          <div className="min-w-0">
            <p className={cn('truncate font-medium', darkMode ? 'text-gray-200' : 'text-gray-700')}>
              {row.primary}
            </p>
            {row.secondary && (
              <p className={cn('truncate text-[10px] mt-0.5', darkMode ? 'text-gray-500' : 'text-gray-400')}>
                {row.secondary}
              </p>
            )}
          </div>
          {columns.middle && (
            <div className={cn('text-right text-[11px]', darkMode ? 'text-gray-400' : 'text-gray-500')}>
              {row.middle}
            </div>
          )}
          {columns.state && (
            <span
              className={cn('text-right font-medium whitespace-nowrap', toneClass(row.tone ?? 'muted', darkMode))}
              title={row.errorMessage || undefined}
            >
              {row.stateLabel}
            </span>
          )}
          {hasActions(rows) && (
            <div className="flex items-center justify-end gap-1 whitespace-nowrap">{row.actions}</div>
          )}
        </div>
      ))
    )}
  </div>
);
